import React from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldRadioButton, FieldSelect } from '../../../components';
import { countryOptions, isPayoutCountrySupported } from '../../../util/payoutCountries';

import css from './SignupForm.module.css';

import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';


// "In which country is your bank account?" - photographers only. Outside the countries we pay
// out in they can still sign up and are put on a waitlist (server/api-util/payoutWaitlist.js).
const PayoutCountryField = ({ intl, country }) => {
  const { supported, other } = countryOptions(intl.locale);
  const waitlisted = !!country && !isPayoutCountrySupported(country);
  return (
    <>
      <FieldSelect
        id="payoutCountry"
        name="payoutCountry"
        className={css.payoutCountry}
        label={intl.formatMessage({ id: 'SignupForm.payoutCountryLabel' })}
        validate={validators.required(intl.formatMessage({ id: 'SignupForm.payoutCountryRequired' }))}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'SignupForm.payoutCountryPlaceholder' })}
        </option>
        <optgroup label={intl.formatMessage({ id: 'SignupForm.payoutCountrySupported' })}>
          {supported.map(c => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </optgroup>
        <optgroup label={intl.formatMessage({ id: 'SignupForm.payoutCountryOther' })}>
          {other.map(c => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </optgroup>
      </FieldSelect>
      <p className={css.payoutCountryHint}>
        {waitlisted ? (
          <FormattedMessage id="SignupForm.payoutCountryWaitlist" />
        ) : (
          <FormattedMessage id="SignupForm.payoutCountryHint" />
        )}
      </p>
    </>
  );
};

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        values,
        customerEmail,
        lastName,
        firstName,
        isCustomer,
        termsAndConditions,
        isInvitedUser,
        form,
        tabs,
        loginOrSignupError,
        onUserRoleChange
      } = fieldRenderProps;

      useEffect(() => {
        const initialRole = values.userRole || initialValues.userRole;
        if (initialRole && typeof onUserRoleChange === 'function') {
          onUserRoleChange(initialRole);
        }
      }, []);

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Derived directly from the form's current values on every render, rather than
      // tracked via separate onChange handlers + state: browser/mobile password-manager
      // autofill can set both password fields without firing the two onChange handlers
      // in the order that stateful approach assumed, letting a genuine mismatch slip
      // through as "matched" (or vice versa). Comparing values directly is immune to
      // that ordering issue.
      const passwordsMatch = values?.password === values?.confirmPassword;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress || (isInvitedUser && !passwordsMatch);

      // Update final-form field and notify parent
      const setUserRole = role => {
        form.change('userRole', role);
        if (typeof onUserRoleChange === 'function') {
          onUserRoleChange(role);
        }
      };

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {loginOrSignupError}
          {tabs}
          <div className={css.signWrapper}>
            <div className={css.radioBox}>
              {isCustomer || isInvitedUser ? null :
                <FieldRadioButton
                  label={intl.formatMessage({ id: 'SignupForm.rolePhotographer' })}
                  className={css.radioSignup}
                  name="userRole"
                  value="photographer"
                  id="photographer"
                  type="radio"
                  onChange={() => setUserRole('photographer')}
                />}
              {isInvitedUser ? null : <FieldRadioButton
                label={intl.formatMessage({ id: 'SignupForm.roleCustomer' })}
                className={css.radioSignup}
                name="userRole"
                value="customer"
                id="customer"
                type="radio"
                onChange={() => setUserRole('customer')}
              />}
            </div>
            <div className={css.signupInputs}>
              {isInvitedUser ? null : <>
                <FieldTextInput
                  className={css.inputBox}
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  disabled={customerEmail ? true : false}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    disabled={firstName ? true : false}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.firstNameRequired',
                      })
                    )}
                  />
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    disabled={lastName ? true : false}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                  />
                </div></>}
              {values.userRole === 'photographer' && !isCustomer && !isInvitedUser ? (
                <PayoutCountryField intl={intl} country={values.payoutCountry} />
              ) : null}
              <FieldTextInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />

              {isInvitedUser ?
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                  name="confirmPassword"
                  label={intl.formatMessage({
                    id: 'SignupForm.confirmPasswordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.confirmPasswordPlaceholder',
                  })}
                  validate={passwordValidators}
                />
                : null
              }
            </div>
          </div>

          {!passwordsMatch && values?.password && values?.confirmPassword && <p><FormattedMessage id="SignupForm.passwordsDoNotMatch" /></p>}
          {termsAndConditions}
          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              {isInvitedUser ? <FormattedMessage id="SignupForm.setPassword" /> : <FormattedMessage id="SignupForm.signUp" />}
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
